import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.hashvi.in';

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('hashvi_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const requestOTP = (phone) =>
  api.post('/auth/request-otp', { phone });

export const requestRegistrationOTP = (phone) =>
  api.post('/auth/request-registration-otp', { phone });

export const verifyOTP = (phone, otp) =>
  api.post('/auth/verify-otp', { phone, otp });

export const getMe = () =>
  api.get('/auth/me');

export const registerLandlord = (data) =>
  api.post('/landlords/register', data);

// Buildings
export const getBuildings = () =>
  api.get('/buildings/');

export const createBuilding = (data) =>
  api.post('/buildings/', data);

// Units
export const getUnits = (buildingId) =>
  api.get('/units/' + buildingId);

export const createUnit = (data) =>
  api.post('/units/', data);

export const updateUnit = (unitId, data) =>
  api.put('/units/' + unitId, data);

export const deleteUnit = (id) =>
  api.delete('/units/' + id);

// Tenants
export const getTenants = () =>
  api.get('/tenants/');

export const createTenant = (data) =>
  api.post('/tenants/', data);

export const updateTenant = (id, data) =>
  api.put('/tenants/' + id, data);

export const deleteTenant = (id) =>
  api.delete('/tenants/' + id);

export const sendReminder = (tenantId) =>
  api.post('/tenants/' + tenantId + '/remind');

export const tenantMoveOut = (tenantId, data) =>
  api.post('/tenants/' + tenantId + '/moveout', data);

export const getTenantPaymentHistory = (tenantId) =>
  api.get('/tenants/' + tenantId + '/payments');

// Rent status
export const getRentStatus = (buildingId, month) =>
  api.get('/rent-status/' + buildingId + '/' + month);

// Payments
export const getPendingPayments = () =>
  api.get('/payments/pending');

export const approvePayment = (paymentId) =>
  api.post('/payments/' + paymentId + '/approve');

export const rejectPayment = (paymentId, reason) =>
  api.post(`/payments/${paymentId}/reject`, { reason });

export const recordPayment = (data) =>
  api.post('/rent-payments/', data);

export const recordManualPayment = (data) =>
  api.post('/payments/manual', data);

// Arrears Notification
export const getBuildingArrears = (buildingId) =>
  api.get('/buildings/' + buildingId + '/arrears');

export const getTenantArrears = (tenantId) =>
  api.get('/tenants/' + tenantId + '/arrears');

export const remindAllArrears = (buildingId) =>
  api.post('/buildings/' + buildingId + '/remind-arrears');

// Excel Report Download
export const downloadRentReport = (month, buildingId, startMonth, endMonth) => {
  const params = {};
  if (month) params.month = month;
  if (buildingId) params.building_id = buildingId;
  if (startMonth) params.start_month = startMonth;
  if (endMonth) params.end_month = endMonth;
  return api.get('/reports/rent-collection', { 
    params,
    responseType: 'blob' 
  });
};

// Deposit payments
export const getDepositPayments = (tenantId) =>
  api.get('/tenants/' + tenantId + '/deposit-payments');

export const recordDepositPayment = (tenantId, data) =>
  api.post('/tenants/' + tenantId + '/deposit-payments', data);

export const deleteDepositPayment = (dpId) =>
  api.delete('/deposit-payments/' + dpId);

// ============================================================================
// v1.6.0: Agreement Tracking API Functions
// ============================================================================

/**
 * Get all agreements with optional status filter
 * @param {string} statusFilter - 'all' | 'in_progress' | 'delivered'
 */
export const getMyAgreements = async (statusFilter = 'all') => {
  const params = statusFilter !== 'all' ? { status_filter: statusFilter } : {};
  const response = await api.get('/agreements/my-agreements', { params });
  return response.data;
};

/**
 * Update delivery status (admin function)
 * @param {number} agreementId - Agreement ID
 * @param {object} statusData - { delivery_status, tracking_number?, courier_company? }
 */
export const updateDeliveryStatus = async (agreementId, statusData) => {
  const response = await api.patch(`/agreements/${agreementId}/update-status`, statusData);
  return response.data;
};

/**
 * Delete agreement (soft delete)
 * @param {number} agreementId - Agreement ID
 */
export const deleteAgreement = async (agreementId) => {
  const response = await api.delete(`/agreements/${agreementId}`);
  return response.data;
};

/**
 * Download agreement PDF
 * @param {number} agreementId - Agreement ID
 * @param {string} tenantName - Tenant name for filename
 */
export const downloadAgreementPDF = async (agreementId, tenantName) => {
  try {
    // ✅ FIX: Use fetch with redirect follow instead of axios
    const token = localStorage.getItem('hashvi_token');
    const response = await fetch(`${API_BASE}/agreements/${agreementId}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      redirect: 'follow'  // Follow redirects to Cloudinary
    });
    
    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }
    
    // Get the blob
    const blob = await response.blob();
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Agreement_${tenantName}_${new Date().getFullYear()}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    console.error('Download error:', error);
    throw error;
  }
};

export default api;
